import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Button from './Button';

/**
 * MultiOptionModal - A reusable modal component with multiple action buttons
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Handler for closing the modal
 * @param {string} title - Modal title
 * @param {string|ReactNode} message - Modal message content (can be string or JSX)
 * @param {Array} options - Array of option objects with structure:
 *   {
 *     text: string,           // Button text
 *     onClick: function,      // Click handler
 *     variant: string,        // Button variant: 'primary', 'outline', 'ghost', 'danger'
 *   }
 * @param {string} type - Modal type: 'danger', 'warning', or 'info'
 * @param {number} maxOptions - Maximum number of options allowed (default: 3)
 */
const MultiOptionModal = ({
  isOpen,
  onClose,
  title = 'Confirm Action',
  message = 'Please choose an option:',
  options = [],
  type = 'warning', // 'danger', 'warning', or 'info'
  maxOptions = 3
}) => {
  if (!isOpen) return null;

  // Limit options to maxOptions
  const limitedOptions = options.slice(0, maxOptions);

  const getIconColor = () => {
    switch(type) {
      case 'danger':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      default:
        return 'text-ag-green';
    }
  };

  const getIconBgColor = () => {
    switch(type) {
      case 'danger':
        return 'bg-red-500/10';
      case 'warning':
        return 'bg-yellow-500/10';
      default:
        return 'bg-ag-green/10';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-ag-black-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-ag-black-600">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ag-black-600">
          <div className="flex items-center gap-3">
            {/* Icon */}
            <div className={`w-10 h-10 rounded-full ${getIconBgColor()} flex items-center justify-center flex-shrink-0`}>
              <AlertTriangle className={getIconColor()} size={20} />
            </div>
            {/* Title */}
            <h2 className="text-lg font-semibold text-white">
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 bg-ag-black-900">
          {/* Message */}
          <div className="text-gray-300 mb-6">
            {typeof message === 'string' ? <p>{message}</p> : message}
          </div>

          {/* Actions - Multiple buttons stacked vertically */}
          <div className="flex flex-col space-y-2">
            {limitedOptions.map((option, index) => (
              <Button
                key={index}
                onClick={option.onClick}
                variant={option.variant || 'outline'}
                className="w-full"
              >
                {option.text}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiOptionModal;